import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar/Sidebar';
import { SidebarItemProps } from '../../components/Sidebar/SidebarItem';
import AccountTopBar from '../../components/AccountTopBar/AccountTopBar';
import UserTable from '../../components/Tables/UserTable';
import Checkbox from '../../components/Checkbox';
import useApi from '../../hooks/use-api';
import { Tag, Tags, User } from '../../utils/props';

export default function Users() {
  const { t } = useTranslation('ns');
  const [userProfileTag, setUserProfileTag] = useState<number | undefined>(undefined);
  const [usersList, setUsersList] = useState<User[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [unapprovedOnly, setUnapprovedOnly] = useState(false);
  
  const getUser = useApi({
    path: '/user',
    callback: (data) => {
      localStorage.setItem('user_id', JSON.stringify(data.id));
      localStorage.setItem('first_name', JSON.stringify(data.first_name));
      localStorage.setItem('last_name', JSON.stringify(data.last_name));
      localStorage.setItem('type', JSON.stringify(data.type));
    }
  })

  const tags = useApi({
    path: '/tags',
    callback: (data) => {
      const profileTag = data.items.find((item: Tag) => item.name === String(Tags.ProfilePicture));
      setUserProfileTag(profileTag?.id);
    }
  })

  const users = useApi({
    path: '/companies',
    callback: (data) => {
      const mappedUsers = data.items.map((item: any) => ({
        id: item.id,
        mail: item.mail,
        phone: item.phone,
        birthdate: item.date_of_birth,
        type: item.type,
        name: `${item.first_name} ${item.last_name}`,
      }));
      setUsersList(mappedUsers);
      setCurrentPage(data.current_page);
      setTotalPages(data.total_pages)
    }
  })

  function fetchUsers(page: number, searchQuery = search, unapprovedOnlyFilter = unapprovedOnly) {
    const query = `?current_page=${page}${searchQuery ? `&query=${encodeURIComponent(searchQuery)}` : ""}${unapprovedOnlyFilter ? "&has_unapproved_files=1" : ""}`;
    users.get(query);
    setCurrentPage(page);
  }

  useEffect(() => {
    fetchUsers(1);
  }, [])

  function handlePrevPage() {
    if (currentPage > 1) {
      fetchUsers(currentPage - 1, search);
    }
  }

  function handleNextPage() {
    if (currentPage < totalPages) {
      fetchUsers(currentPage + 1, search);
    }
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    fetchUsers(1, search);
  }

  function handleUnapprovedOnlyChange(e: React.FormEvent<HTMLInputElement>) {
    const checked = e.currentTarget.checked;
    setUnapprovedOnly(checked);
    fetchUsers(1, search, checked);
  }

  const deleteUserApi = useApi({
    path: '/company',
    callback: () => {
      fetchUsers(currentPage, search);
    }
  });

  function onDelete(id: string | number) {
    deleteUserApi.deleteApi(id);
  }

  useEffect(() => {
    getUser.get();
    tags.get();
    users.get();
  }, [])

  const sidebarItems: SidebarItemProps[] = [
    {
      label: 'Users',
      href: '#users',
    },
  ]

  return (
    <>
      <AccountTopBar showNav={false} />
      <div className="pd:mx-0 min-h-screen w-full bg-white px-5 md:flex lg:px-[190px]">
      <div className="flex-shrink-0 bg-white md:min-w-[260px]">
        <Sidebar
          userName={`${localStorage.getItem('first_name')?.replace(/['"]+/g, '') ?? ''} ${localStorage.getItem('last_name')?.replace(/['"]+/g, '') ?? ''}`}
          items={sidebarItems}
          tagId={userProfileTag ?? 0}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-start py-12 md:ml-16">
        <div className="w-full min-w-0">
          <h2 id="users" className="mb-1 text-2xl font-bold">
            {t('Users')}
          </h2>
          <form onSubmit={handleSearchSubmit} className="mb-4 flex flex-wrap items-center gap-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search users..."
                className="border border-gray-300 rounded px-3 py-2"
              />
              <button
                type="submit"
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Search
              </button>
            </div>
            <Checkbox
              id="unapproved-only"
              checked={unapprovedOnly}
              onChange={handleUnapprovedOnlyChange}
              label="Only show users with unapproved files"
            />
          </form>
          <UserTable 
            users={usersList}
            onDelete={onDelete}
            rowsPerPage={10}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            currentPage={currentPage}
            totalPages={totalPages}
          />
        </div>
      </div>
      </div>
    </>
  )
}